<script>
	import { onMount } from "svelte";
	import { deviceSerialAll as all, deviceDistributionAll as distributionAll, menu, menuSub, menuSubSub } from "../../stores";
	import { postApi as post, putApi as put, delApi as del } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { fade } from "svelte/transition";
	import { ErrorCodes } from "../../define/define";

	// 상수 정의
	const BASE_URL = "/device/serial";
	const DISTRIBUTION_URL = "/device/distribution";
	const PAGE_SIZES = [20, 25, 30, 50, 100];
	const LOCALE_INFO = {
		KR: { value: "KR", text: "한국" },
		US: { value: "US", text: "미국" },
	};
	const ACTIVE_OPTIONS = [
		{ value: true, text: "사용" },
		{ value: false, text: "미사용" },
	];

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let focusField = "deviceSn";
	let filteredData = [];
	let distributionList = [];
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 20;

	// 편집 아이템 초기값
	const defaultEditItem = {
		ID: 0,
		deviceSn: "",
		localeInfo: "KR",
		idDistribution: "",
		isActive: true,
	};

	let editItem = { ...defaultEditItem };

	// 모달 상태 관리 추가
	let showBulkModal = false;
	let bulkSerials = ""; // 여러 줄의 시리얼 입력을 위한 상태

	// 로딩 상태 추가
	let isLoading = false;

	// 검색 조건 상태 추가
	let searchParams = {
		deviceSn: "",
		localeInfo: "ALL",
		idDistribution: 0,
	};

	// 유통채널 데이터 로드
	const loadDistributions = async () => {
		await distributionAll.fetch(`${DISTRIBUTION_URL}/all/100/1`);
		const data = $distributionAll?.data || {};
		distributionList = data.list || [];
	};

	// 메서드
	const setFocus = (field = "deviceSn") => {
		focusField = field;
	};

	const validateItem = () => {
		if (!editItem.deviceSn) {
			alert("시리얼을 입력하세요.");
			setFocus("deviceSn");
			return false;
		}
		if (!editItem.idDistribution) {
			alert("유통채널을 선택하세요.");
			setFocus("idDistribution");
			return false;
		}
		return true;
	};

	const handleSave = async () => {
		if (!validateItem()) return;

		const payload = {
			...editItem,
			isActive: Boolean(editItem.isActive),
		};

		try {
			if (editingRowId) {
				console.log("PUT 요청:", payload);
				await put(BASE_URL, payload);
			} else {
				console.log("POST 요청:", payload);
				await post(BASE_URL, payload);
			}
			await fnSearch();
		} catch (error) {
			console.error("저장 중 오류 발생:", error);
			alert("저장 중 오류가 발생했습니다.");
		}
	};

	// 검색 처리 함수
	const handleSearch = async () => {
		currentPage = 1; // 검색 시 첫 페이지로 이동
		await fnSearch();
	};

	// fnSearch 함수 수정
	const fnSearch = async () => {
		let deviceSn = searchParams.deviceSn === "" ? "ALL" : searchParams.deviceSn;
		let url = `${BASE_URL}/${searchParams.localeInfo}/${searchParams.idDistribution}/${pageSize}/${currentPage}/${deviceSn}`;

		await all.fetch(url);
		resetEdit();
	};

	// 검색 조건 초기화
	const resetSearch = () => {
		searchParams = {
			deviceSn: "",
			localeInfo: "ALL",
			idDistribution: 0,
		};
		handleSearch();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
	};

	const fnProcessRegist = () => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = null;
			editItem = { ...defaultEditItem };
			focusFirstInput();
		}
	};

	const focusFirstInput = () => {
		setTimeout(() => {
			const nameInput = document.querySelector('input[name="deviceSn"]');
			if (nameInput) {
				nameInput.focus();
				nameInput.select();
			}
		});
	};

	const fnProcessEdit = (item) => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = item.ID;
			editItem = {
				ID: item.ID,
				deviceSn: item.deviceSn,
				localeInfo: item.localeInfo,
				idDistribution: item.idDistribution,
				isActive: item.isActive,
			};
			focusFirstInput();
		}
	};

	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.ID === id);
		if (item.isActive) {
			alert("사용 중인 디바이스 시리얼은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await del(BASE_URL, { ID: id });
			await fnSearch();
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			put(`${BASE_URL}/cache`, {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1; // 페이지를 1로 리셋
		fnSearch(); // 목록 새로고침
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	// 유통채널명 조회
	const getDistributionName = (id) => {
		const distribution = distributionList.find((d) => d.ID === id);
		return distribution ? distribution.name : "없음";
	};

	// 모달 토글 함수
	const toggleBulkModal = () => {
		showBulkModal = !showBulkModal;
		if (!showBulkModal) {
			bulkSerials = ""; // 모달 닫을 때 입력값 초기화
		}
	};

	// 일괄 등록 처리 함수 수정
	const handleBulkSave = async () => {
		if (!editItem.localeInfo) {
			alert("지역 정보를 선택하세요.");
			return;
		}
		if (!editItem.idDistribution) {
			editItem.idDistribution = 0;
		}
		if (!bulkSerials.trim()) {
			alert("시리얼을 입력하세요.");
			return;
		}

		const serials = bulkSerials
			.split("\n")
			.map((line) => line.trim())
			.filter((line) => line.length > 0);

		if (serials.length === 0) {
			alert("유효한 시리얼이 없습니다.");
			return;
		}

		try {
			isLoading = true; // 로딩 시작
			let result;
			let errorStack = [];
			let successCnt = 0;
			let failCnt = 0;
			// 선택된 유통채널 정보로 각 시리얼에 대한 등록 요청
			for (const serial of serials) {
				const payload = {
					...defaultEditItem,
					deviceSn: serial,
					localeInfo: editItem.localeInfo,
					idDistribution: editItem.idDistribution,
					isActive: true,
				};
				result = await post(BASE_URL, payload);
				if (result.resultCode !== ErrorCodes.OK) {
					errorStack.push(result.resultDescKo);
					failCnt++;
				} else {
					successCnt++;
				}
			}
			alert(`${successCnt}개의 시리얼이 등록되었습니다. ${failCnt}개의 시리얼이 등록에 실패했습니다.`);
			if (errorStack.length > 0) {
				alert(errorStack.join("\n"));
			}
			toggleBulkModal();
			await fnSearch();
		} catch (error) {
			console.error("일괄 등록 중 오류 발생:", error);
			alert("일괄 등록 중 오류가 발생했습니다.");
		} finally {
			isLoading = false; // 로딩 종료
		}
	};

	// 유통채널 선택 시 지역 정보 자동 설정
	const handleDistributionChange = (event) => {
		const selectedId = event.target.value;
		if (selectedId) {
			const selectedDist = distributionList.find((d) => d.ID === parseInt(selectedId));
			if (selectedDist) {
				editItem.localeInfo = selectedDist.localeInfo;
			}
		} else {
			editItem.localeInfo = "KR"; // 기본값으로 리셋
		}
	};

	$: {
		const data = $all?.data || {};
		filteredData = data.list || [];
		totalPages = Math.ceil((data.total || 0) / pageSize);
		totalItems = data.total || 0;
	}

	onMount(async () => {
		menu.set("디바이스 관리");
		menuSub.set("시리얼 관리");
		menuSubSub.set("");
		await loadDistributions();
		await fnSearch();
	});
</script>

<div class="wrapper">
	<!-- 메인 컨텐츠 영역 -->
	<div class="content-area" class:inactive={showBulkModal}>
		<div class="card mb-4">
			<div class="table-responsive text-nowrap">
				<!-- 검색 영역 추가 -->
				<div class="p-3 bg-light border-bottom">
					<div class="row g-3" style="width: 60%;">
						<div class="col-md-3">
							<label class="form-label" for="searchDeviceSn">시리얼</label>
							<input
								type="text"
								id="searchDeviceSn"
								class="form-control form-control-sm"
								placeholder="시리얼 검색"
								bind:value={searchParams.deviceSn}
							/>
						</div>
						<div class="col-md-2">
							<label class="form-label" for="searchLocaleInfo">지역 정보</label>
							<select id="searchLocaleInfo" class="form-select form-select-sm" bind:value={searchParams.localeInfo}>
								<option value="ALL">전체</option>
								{#each Object.values(LOCALE_INFO) as { value, text }}
									<option {value}>{text}</option>
								{/each}
							</select>
						</div>
						<div class="col-md-3">
							<label class="form-label" for="searchDistribution">유통채널</label>
							<select id="searchDistribution" class="form-select form-select-sm" bind:value={searchParams.idDistribution}>
								<option value="0">전체</option>
								{#each distributionList as dist}
									<option value={dist.ID}>{dist.name}</option>
								{/each}
							</select>
						</div>
						<div class="col-md-4 d-flex align-items-end">
							<button class="btn btn-primary btn-sm me-2" on:click={handleSearch}>검색</button>
							<button class="btn btn-outline-secondary btn-sm" on:click={resetSearch}>초기화</button>
						</div>
					</div>
				</div>

				<!-- 기존 TableToolbar -->
				<div class="ms-auto" style="padding: 5px;">
					<TableToolbar
						{totalItems}
						{currentPage}
						{totalPages}
						{pageSize}
						pageSizes={PAGE_SIZES}
						onPageSizeChange={handlePageSizeChange}
						onReload={fnSearch}
						onRegist={fnProcessRegist}
						onRegistServer={fnRegistServer}
						onBulkRegist={toggleBulkModal}
					/>
				</div>

				<!-- 테이블 영역 -->
				<div class="fade-container show">
					<table class="table table-sm">
						<thead>
							<tr style="text-align:center; font-size: 12px;">
								<th width="80">No</th>
								<th width="*">시리얼</th>
								<th width="120">지역 정보</th>
								<th width="180">유통채널명</th>
								<th width="120">사용 여부</th>
								<th width="150">등록일시</th>
								<th width="150">수정일시</th>
								<th width="150">관리</th>
							</tr>
						</thead>
						<tbody>
							{#if isEditMode && editingRowId === null}
								<tr style="text-align:center">
									<td />
									<td>
										<input
											type="text"
											name="deviceSn"
											class="form-control form-control-sm"
											bind:value={editItem.deviceSn}
											on:focus={() => setFocus("deviceSn")}
										/>
									</td>
									<td>
										<select
											class="form-select form-select-sm"
											bind:value={editItem.localeInfo}
											disabled={!!editItem.idDistribution}
										>
											{#each Object.values(LOCALE_INFO) as { value, text }}
												<option {value}>{text}</option>
											{/each}
										</select>
									</td>
									<td>
										<select
											class="form-select form-select-sm"
											bind:value={editItem.idDistribution}
											on:change={handleDistributionChange}
										>
											<option value="">선택하세요</option>
											{#each distributionList as dist}
												<option value={dist.ID}>{dist.name} ({LOCALE_INFO[dist.localeInfo]?.text})</option>
											{/each}
										</select>
									</td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" id="isActive-new" bind:checked={editItem.isActive} />
										</div>
									</td>
									<td />
									<td />
									<td>
										<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
										<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
									</td>
								</tr>
							{/if}

							{#if filteredData.length === 0}
								<tr>
									<td colspan="8" class="text-center py-4" style="font-size: 12px;">데이터가 없습니다</td>
								</tr>
							{:else}
								{#each filteredData as item, index}
									{#if editingRowId === item.ID}
										<tr style="text-align:center; font-size: 12px;">
											<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
											<td>
												<input
													type="text"
													name="deviceSn"
													class="form-control form-control-sm text-center"
													bind:value={editItem.deviceSn}
													on:focus={() => setFocus("deviceSn")}
													disabled={!!editingRowId}
												/>
											</td>
											<td>
												<select
													class="form-select form-select-sm"
													bind:value={editItem.localeInfo}
													disabled={!!editItem.idDistribution}
												>
													{#each Object.values(LOCALE_INFO) as { value, text }}
														<option {value}>{text}</option>
													{/each}
												</select>
											</td>
											<td>
												<select
													class="form-select form-select-sm"
													bind:value={editItem.idDistribution}
													on:change={handleDistributionChange}
													on:focus={() => setFocus("idDistribution")}
												>
													<option value="">선택하세요</option>
													{#each distributionList as dist}
														<option value={dist.ID}>{dist.name} ({LOCALE_INFO[dist.localeInfo]?.text})</option>
													{/each}
												</select>
											</td>
											<td>
												<div class="form-check form-switch d-flex justify-content-center">
													<input
														class="form-check-input"
														type="checkbox"
														id="isActive-edit"
														bind:checked={editItem.isActive}
													/>
												</div>
											</td>
											<td>{Dates.defaultConvertFull(item.CreatedAt)}</td>
											<td>{Dates.defaultConvertFull(item.UpdatedAt)}</td>
											<td>
												<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
												<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
											</td>
										</tr>
									{:else}
										<tr style="text-align:center; font-size: 12px;">
											<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
											<td>{item.deviceSn}</td>
											<td><b>{LOCALE_INFO[item.localeInfo]?.text || item.localeInfo}</b></td>
											<td>{getDistributionName(item.idDistribution)}</td>
											<td>
												<div class="form-check form-switch d-flex justify-content-center">
													<input class="form-check-input" type="checkbox" checked={item.isActive} disabled readonly />
												</div>
											</td>
											<td>{Dates.defaultConvertFull(item.CreatedAt)}</td>
											<td>{Dates.defaultConvertFull(item.UpdatedAt)}</td>
											<td>
												<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>Edit</button>
												<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.ID)}>Delete</button>
											</td>
										</tr>
									{/if}
								{/each}
							{/if}
						</tbody>
					</table>
				</div>
				<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
			</div>
		</div>
	</div>

	<!-- 모달 영역 -->
	{#if showBulkModal}
		<div class="modal-overlay">
			<div
				class="modal fade {showBulkModal ? 'show' : ''}"
				style="display: block;"
				tabindex="-1"
				aria-modal="true"
				role="dialog"
				transition:fade={{ duration: 200 }}
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">시리얼 일괄 등록</h5>
							<button type="button" class="btn-close" on:click={toggleBulkModal} disabled={isLoading} />
						</div>
						<div class="modal-body">
							<div class="mb-3">
								<label class="form-label" for="modalDistribution">유통채널</label>
								<select
									id="modalDistribution"
									class="form-select form-select-sm"
									bind:value={editItem.idDistribution}
									on:change={handleDistributionChange}
								>
									<option value="">선택하세요</option>
									{#each distributionList as dist}
										<option value={dist.ID}>{dist.name} ({LOCALE_INFO[dist.localeInfo]?.text})</option>
									{/each}
								</select>
							</div>
							<div class="mb-3">
								<label class="form-label" for="modalLocaleInfo">지역 정보</label>
								<select
									id="modalLocaleInfo"
									class="form-select form-select-sm"
									bind:value={editItem.localeInfo}
									disabled={!!editItem.idDistribution}
								>
									{#each Object.values(LOCALE_INFO) as { value, text }}
										<option {value}>{text}</option>
									{/each}
								</select>
							</div>
							<div class="mb-3">
								<label class="form-label" for="modalSerials">시리얼 목록 (한 줄에 하나씩 입력)</label>
								<textarea
									id="modalSerials"
									class="form-control"
									rows="10"
									placeholder="시리얼을 입력하세요"
									bind:value={bulkSerials}
								/>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" on:click={toggleBulkModal} disabled={isLoading}>취소</button>
							<button type="button" class="btn btn-primary" on:click={handleBulkSave} disabled={isLoading}>
								{#if isLoading}
									<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									등록 중...
								{:else}
									등록
								{/if}
							</button>
						</div>
					</div>
				</div>
			</div>
			<div class="modal-backdrop fade show" />
		</div>
	{/if}
</div>

<style>
	.wrapper {
		position: relative;
		width: 100%;
		height: 100%;
	}

	.content-area.inactive {
		pointer-events: none;
		opacity: 0.6;
		filter: blur(1px);
	}

	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 1040;
	}

	.modal {
		z-index: 1045;
	}

	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
	}

	.fade-container {
		opacity: 0;
		transition: opacity 0.15s linear;
	}

	.fade-container.show {
		opacity: 1;
	}

	.spinner-border {
		width: 1.5rem;
		height: 1.5rem;
	}

	.bg-light {
		background-color: #f8f9fa;
	}
</style>
